import { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/context/LanguageContext";
import { logger } from "@/lib/logger";
import { PHOTON, BIAS, airportPlace, toPlace, rank } from "@/lib/geocode";
import { MapPin, LocateFixed, Loader2, Plane, Building2, Store, X, Navigation } from "lucide-react";

const ICONS = { airport: Plane, hotel: Building2, business: Store, city: Building2, address: MapPin, place: MapPin };

function Highlight({ text, query }) {
  if (!query) return <>{text}</>;
  const i = text.toLowerCase().indexOf(query.toLowerCase());
  if (i === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <span className="text-white font-medium">{text.slice(i, i + query.length)}</span>
      {text.slice(i + query.length)}
    </>
  );
}

function AddressDropdown({ loading, results, showNoResults, active, query, onHover, onChoose, testId, t }) {
  return (
    <div className="absolute z-40 mt-2 w-full rounded-lg border border-white/15 bg-popover backdrop-blur-2xl shadow-2xl overflow-hidden max-h-80 overflow-y-auto" data-testid={`${testId}-suggestions`}>
      {loading && (
        <div className="px-4 py-3 text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("ac.searching")}
        </div>
      )}
      {!loading && results.map((pl, i) => {
        const Icon = ICONS[pl.locationType] || MapPin;
        const secondary = pl.formattedAddress.startsWith(pl.placeName)
          ? pl.formattedAddress.slice(pl.placeName.length).replace(/^,\s*/, "")
          : pl.formattedAddress;
        return (
          <button
            key={`${pl.placeId}-${i}`}
            type="button"
            data-testid={`${testId}-option-${i}`}
            onMouseEnter={() => onHover(i)}
            onClick={() => onChoose(pl)}
            className={`w-full text-left px-4 py-3 text-sm transition-colors flex items-start gap-3 border-b border-white/5 last:border-0 ${active === i ? "bg-white/10" : "hover:bg-white/10"}`}
          >
            <Icon className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
            <span className="min-w-0">
              <span className="block text-white/90 truncate"><Highlight text={pl.placeName} query={query} /></span>
              {secondary && <span className="block text-muted-foreground text-xs truncate">{secondary}</span>}
            </span>
          </button>
        );
      })}
      {showNoResults && (
        <div className="px-4 py-3 text-sm text-muted-foreground flex items-center gap-2" data-testid={`${testId}-noresults`}>
          <Navigation className="h-3.5 w-3.5" /> {t("ac.noResults")}
        </div>
      )}
    </div>
  );
}

export function AddressAutocomplete({ value, onChange, onSelect, placeholder, testId }) {
  const { t, lang } = useLanguage();
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [active, setActive] = useState(-1);
  const [searched, setSearched] = useState(false);
  const wrapRef = useRef(null);
  const abortRef = useRef(null);
  const skipNext = useRef(false);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const runSearch = useCallback(async (q) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const url = `${PHOTON}/api/?q=${encodeURIComponent(q)}&limit=6&lang=${lang === "fr" ? "fr" : "en"}&lat=${BIAS.lat}&lon=${BIAS.lon}`;
      const res = await fetch(url, { signal: controller.signal });
      const data = await res.json();
      let places = rank((data.features || []).map(toPlace));
      const code = q.trim().toUpperCase();
      const ap = /^[A-Z]{3}$/.test(code) ? airportPlace(code) : null;
      if (ap && !places.some((pl) => pl.locationType === "airport")) places = [ap, ...places];
      setResults(places);
      setSearched(true);
      setOpen(true);
      setActive(-1);
    } catch (e) {
      if (e.name !== "AbortError") { logger.error("Address search failed:", e); setResults([]); setSearched(true); }
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    if (!query || query.trim().length < 3) { setResults([]); setSearched(false); return; }
    const timer = setTimeout(() => runSearch(query), 300);
    return () => clearTimeout(timer);
  }, [query, runSearch]);

  const choose = (place) => {
    skipNext.current = true;
    setQuery(place.formattedAddress);
    onChange(place.formattedAddress);
    onSelect && onSelect({ ...place, address: place.formattedAddress, coords: { lat: place.latitude, lng: place.longitude } });
    setOpen(false);
    setActive(-1);
  };

  const handleType = (v) => {
    setQuery(v);
    onChange(v);
    onSelect && onSelect(null); // invalidate any prior selection until a suggestion is picked
  };

  const clear = () => {
    skipNext.current = true;
    setQuery("");
    onChange("");
    onSelect && onSelect(null);
    setResults([]);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); choose(results[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const res = await fetch(`${PHOTON}/reverse?lat=${latitude}&lon=${longitude}&lang=${lang === "fr" ? "fr" : "en"}`);
          const data = await res.json();
          const f = data.features?.[0];
          if (f) choose(toPlace(f));
        } catch (e) {
          logger.error("Reverse geocoding failed:", e);
        }
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const showNoResults = open && !loading && searched && results.length === 0;

  return (
    <div className="relative" ref={wrapRef}>
      <MapPin className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground z-10" />
      <Input
        data-testid={testId}
        value={query}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        onChange={(e) => handleType(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onKeyDown={onKeyDown}
        className="h-12 pl-10 pr-16 bg-secondary border-white/10 text-white"
      />
      <div className="absolute right-2 top-2.5 flex items-center gap-0.5">
        {query && (
          <button type="button" data-testid={`${testId}-clear`} onClick={clear} title="Clear"
            className="h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-white hover:bg-white/10 transition-colors">
            <X className="h-4 w-4" />
          </button>
        )}
        <button type="button" data-testid={`${testId}-locate`} onClick={useMyLocation} title="Use my current location"
          className="h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-white hover:bg-white/10 transition-colors">
          {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}
        </button>
      </div>

      {open && (loading || results.length > 0 || showNoResults) && (
        <AddressDropdown
          loading={loading} results={results} showNoResults={showNoResults} active={active}
          query={query} onHover={setActive} onChoose={choose} testId={testId} t={t}
        />
      )}
    </div>
  );
}
